import logo from '../assets/logo.png'
import styles from './Screens.module.css'

/**
 * Jednoduché obrazovky kolem přehrávače.
 *
 * Nic tu nepočítá rozměry v JavaScriptu — původní aplikace odvozovala
 * velikost písma i pozice z `window.innerWidth` při renderu a nereagovala
 * na `resize`, takže první kliknutí na SPUSTIT vždy minulo tlačítko.
 */

export type Language = 'cze' | 'eng'

interface ShellProps {
  children: React.ReactNode
}

function Shell({ children }: ShellProps): React.ReactElement {
  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <img className={styles.logo} src={logo} alt="SafeTutor" />
        {children}
      </div>
    </main>
  )
}

/* ------------------------------------------------------------------ */

export function LoadingScreen({ label }: { label?: string }): React.ReactElement {
  return (
    <Shell>
      <div className={styles.spinner} role="status" aria-live="polite" />
      <p className={styles.text}>{label ?? 'Načítám kurz…'}</p>
    </Shell>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Mezikrok mezi poslední otázkou a verdiktem.
 *
 * Původně naskočil výsledek skokem a uživatel často nepoznal, že kurz
 * vůbec skončil. Krátké „Modul dokončen — vyhodnocuji výsledky…“ dá
 * jasný signál konce a pak se teprve odkryje, jestli prošel.
 */
export function ResultsPendingScreen({ language }: { language: Language }): React.ReactElement {
  const cze = language === 'cze'
  return (
    <Shell>
      <h1 className={styles.title}>{cze ? 'Modul dokončen' : 'Module completed'}</h1>
      <div className={styles.spinner} role="status" aria-live="polite" />
      <p className={styles.text}>
        {cze ? 'Vyhodnocuji vaše výsledky…' : 'Evaluating your results…'}
      </p>
    </Shell>
  )
}

/* ------------------------------------------------------------------ */

export interface LaunchScreenProps {
  title: string
  language: Language
  onStart: () => void
  /** Nabídka pokračování v rozdělaném kurzu. */
  resume?: { onResume: () => void; label: string } | undefined
}

export function LaunchScreen({
  title,
  language,
  onStart,
  resume,
}: LaunchScreenProps): React.ReactElement {
  return (
    <Shell>
      <h1 className={styles.title}>{title}</h1>
      {resume && (
        <>
          <p className={styles.text}>{resume.label}</p>
          <button type="button" className={styles.button} onClick={resume.onResume}>
            {language === 'eng' ? 'CONTINUE' : 'POKRAČOVAT'}
          </button>
        </>
      )}
      <button
        type="button"
        className={`${styles.button} ${resume ? styles.secondary : ''}`}
        onClick={onStart}
      >
        {language === 'eng' ? (resume ? 'START OVER' : 'LAUNCH') : resume ? 'ZAČÍT ZNOVU' : 'SPUSTIT'}
      </button>
    </Shell>
  )
}

/* ------------------------------------------------------------------ */

export interface LoginScreenProps {
  username: string
  onChange: (value: string) => void
  onSubmit: () => void
  failed: boolean
  busy: boolean
}

export function LoginScreen({
  username,
  onChange,
  onSubmit,
  failed,
  busy,
}: LoginScreenProps): React.ReactElement {
  return (
    <Shell>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <label className={styles.text} htmlFor="username">
          Uživatelské jméno
        </label>
        <input
          id="username"
          name="username"
          className={styles.input}
          type="text"
          autoComplete="username"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          value={username}
          onChange={(e) => onChange(e.target.value)}
          disabled={busy}
        />
        <button type="submit" className={styles.button} disabled={busy || username.trim() === ''}>
          {busy ? 'PŘIHLAŠUJI…' : 'PŘIHLÁSIT'}
        </button>
        {failed && (
          <p className={styles.error} role="alert">
            Přihlášení se nezdařilo. Zkontrolujte své přihlašovací jméno.
          </p>
        )}
      </form>
    </Shell>
  )
}

/* ------------------------------------------------------------------ */

export interface ErrorScreenProps {
  title: string
  detail: string
  onRetry?: (() => void) | undefined
  onSkip?: (() => void) | undefined
  skipLabel?: string
}

/** Z chybového stavu musí vždy vést cesta ven — nikdy bílá plocha. */
export function ErrorScreen({
  title,
  detail,
  onRetry,
  onSkip,
  skipLabel,
}: ErrorScreenProps): React.ReactElement {
  return (
    <Shell>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.text} role="alert">
        {detail}
      </p>
      <div className={styles.actions}>
        {onRetry && (
          <button type="button" className={styles.button} onClick={onRetry}>
            Zkusit znovu
          </button>
        )}
        {onSkip && (
          <button
            type="button"
            className={`${styles.button} ${styles.secondary}`}
            onClick={onSkip}
          >
            {skipLabel ?? 'Pokračovat'}
          </button>
        )}
      </div>
    </Shell>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Kam verdikt vede.
 *  - `profile` — ostrý kurz: přejde se na dokončovací adresu, server zapíše
 *    školení a přesměruje do profilu s certifikátem.
 *  - `demo` — ukázkový kurz: nic se nezapisuje, přejde se na `successUrl`.
 *  - `manual` — vývojářská pomůcka: samo se nepřechází.
 */
export type FinishDestination = 'profile' | 'demo' | 'manual'

export interface FinishedScreenProps {
  passed: boolean
  percent: number
  language: Language
  destination: FinishDestination
  onContinue: () => void
}

export function FinishedScreen({
  passed,
  percent,
  language,
  destination,
  onContinue,
}: FinishedScreenProps): React.ReactElement {
  const cze = language === 'cze'
  const continueLabel =
    destination === 'profile'
      ? cze
        ? 'POKRAČOVAT DO PROFILU'
        : 'CONTINUE TO PROFILE'
      : destination === 'demo'
        ? cze
          ? 'POKRAČOVAT'
          : 'CONTINUE'
        : cze
          ? 'DOKONČIT'
          : 'FINISH'
  return (
    <Shell>
      <div
        className={`${styles.verdictIcon} ${passed ? styles.verdictPass : styles.verdictFail}`}
        aria-hidden="true"
      >
        {passed ? '✓' : '✕'}
      </div>
      <h1 className={styles.title}>
        {passed
          ? cze
            ? 'Úspěšně jste dokončili kurz'
            : 'You passed the course'
          : cze
            ? 'Kurz jste nesplnili'
            : 'Course not passed'}
      </h1>
      <div className={styles.progress}>
        <div className={styles.progressBar} style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
      <p className={styles.text}>
        {cze ? 'Úspěšnost' : 'Score'}: {Math.round(percent)} %
      </p>
      {/* Certifikát vzniká až zápisem na serveru — neslibovat hotovou věc.
          Demo ani vývojářská pomůcka žádný certifikát nemají. */}
      {passed && destination === 'profile' && (
        <p className={styles.certNote}>
          {cze
            ? '✓ Certifikát najdete v profilu, kam vás teď přesměrujeme.'
            : '✓ You will find your certificate in your profile, where we are taking you now.'}
        </p>
      )}
      {!passed && (
        <p className={styles.text}>
          {cze
            ? 'Kurz můžete po dokončení spustit znovu.'
            : 'You can retake the course after finishing.'}
        </p>
      )}
      {destination !== 'manual' && (
        <p className={styles.text} role="status">
          {destination === 'profile'
            ? cze
              ? 'Ukládám výsledek a přesměrovávám vás do profilu…'
              : 'Saving your result and taking you to your profile…'
            : cze
              ? 'Přesměrovávám vás dál…'
              : 'Redirecting you…'}
        </p>
      )}
      <button type="button" className={styles.button} onClick={onContinue}>
        {continueLabel}
      </button>
    </Shell>
  )
}
